import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertTriangle, Package, Search, X } from "lucide-react";
import toast from "react-hot-toast";

import { UserAvatar } from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { SpinnerBlock } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useBrand } from "@/contexts/BrandContext/hooks";
import { cn } from "@/lib/utils";
import {
  checkTikTokCollaborationConflicts,
  createTikTokCollaboration,
  getTikTokOutreachQuota,
  listTikTokAffiliateProducts,
  listTikTokSamples,
  type TikTokCreatorPick,
} from "@/services/tiktokShop.service";
import { formatCompactCount } from "@/utils/formatters";

import { TikTokBlockedState } from "../TikTokBlockedState";
import { blockFromError, errorMessage, shortDate } from "../tiktokFormat";

const MAX_CREATORS = 50;
const MESSAGE_MAX = 500;

interface ProductTerms {
  selected: boolean;
  commission: string;
  adsCommission: string;
}

const isoDate = (d: Date) => d.toISOString().slice(0, 10);

/** Creators the brand already deals with: everyone who requested a sample. Inbox contacts carry no open id, so they cannot be invited from here. */
function useSuggestedCreators(enabled: boolean): TikTokCreatorPick[] {
  const inProgress = useQuery({ queryKey: ["tiktok-samples", "IN_PROGRESS"], queryFn: () => listTikTokSamples("IN_PROGRESS"), enabled });
  const completed = useQuery({ queryKey: ["tiktok-samples", "COMPLETED"], queryFn: () => listTikTokSamples("COMPLETED"), enabled });
  const pending = useQuery({ queryKey: ["tiktok-samples", "TO_REVIEW"], queryFn: () => listTikTokSamples("TO_REVIEW"), enabled });
  return useMemo(() => {
    const seen = new Map<string, TikTokCreatorPick>();
    for (const s of [...(completed.data?.items ?? []), ...(inProgress.data?.items ?? []), ...(pending.data?.items ?? [])]) {
      if (!seen.has(s.creator.openId)) {
        seen.set(s.creator.openId, {
          openId: s.creator.openId,
          username: s.creator.username,
          nickname: s.creator.nickname,
          avatarUrl: s.creator.avatarUrl,
          followerCount: s.creator.followerCount,
        });
      }
    }
    return [...seen.values()];
  }, [completed.data, inProgress.data, pending.data]);
}

/**
 * A TikTok collab invite: a private commission offer, with optional free
 * samples, on chosen products to up to 50 chosen creators. TikTok delivers it
 * to the creators' Creator Center; nothing goes to them from Reveshare.
 */
export function NewInviteSheet({
  open,
  onOpenChange,
  initialCreators,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialCreators: TikTokCreatorPick[];
  onCreated: (id: string | null) => void;
}) {
  const { brandDetails } = useBrand();
  const brandName = brandDetails?.companyName || brandDetails?.username || "Our shop";
  const products = useQuery({ queryKey: ["tiktok-affiliate-products"], queryFn: listTikTokAffiliateProducts, enabled: open });
  const quota = useQuery({ queryKey: ["tiktok-outreach-quota"], queryFn: getTikTokOutreachQuota, enabled: open });
  const suggested = useSuggestedCreators(open);

  const [creators, setCreators] = useState<TikTokCreatorPick[]>([]);
  const [filter, setFilter] = useState("");
  const [terms, setTerms] = useState<Record<string, ProductTerms>>({});
  const [name, setName] = useState("");
  const [endDate, setEndDate] = useState(isoDate(new Date(Date.now() + 30 * 86_400_000)));
  const [message, setMessage] = useState("");
  const [freeSample, setFreeSample] = useState(true);
  const [autoApprove, setAutoApprove] = useState(true);
  const [contentType, setContentType] = useState<"ANY" | "VIDEO" | "LIVE">("ANY");
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (!open) return;
    setCreators(initialCreators.slice(0, MAX_CREATORS));
    setFilter("");
    setName(`${brandName} creator collab · ${new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" })}`);
    setMessage(`Hi! We love your content and think our products are a great fit for your audience. We’d love to send you a free sample and offer you a higher commission.`);
    setEmail(brandDetails?.email ?? "");
    setTerms({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!products.data) return;
    setTerms((prev) => {
      const next = { ...prev };
      for (const p of products.data) {
        if (!next[p.id]) {
          const base = Math.min(80, Math.max(10, (p.openCommission ?? 10) + 5));
          next[p.id] = { selected: false, commission: String(base), adsCommission: "" };
        }
      }
      return next;
    });
  }, [products.data]);

  const selectedProducts = (products.data ?? []).filter((p) => terms[p.id]?.selected);
  const creatorIds = creators.map((c) => c.openId);
  const productIds = selectedProducts.map((p) => p.id);
  const conflictKey = [...creatorIds].sort().join(",") + "|" + [...productIds].sort().join(",");
  const conflicts = useQuery({
    queryKey: ["tiktok-collab-conflicts", conflictKey],
    queryFn: () => checkTikTokCollaborationConflicts(creatorIds, productIds),
    enabled: open && creatorIds.length > 0 && productIds.length > 0,
    staleTime: 60_000,
  });

  const maxFollowers = quota.data?.maxFollowerCount ?? null;
  const overLimit = creators.filter((c) => maxFollowers !== null && (c.followerCount ?? 0) > maxFollowers);
  const remaining = quota.data?.unlimited ? Infinity : quota.data?.remaining ?? Infinity;

  const invalidCommission = selectedProducts.some((p) => {
    const n = Number(terms[p.id].commission);
    const ads = terms[p.id].adsCommission.trim();
    return !Number.isInteger(n) || n < 1 || n > 80 || (ads !== "" && (!Number.isInteger(Number(ads)) || Number(ads) < 1 || Number(ads) > 80));
  });
  const problems = [
    creators.length === 0 && "Pick at least one creator",
    selectedProducts.length === 0 && "Pick at least one product",
    invalidCommission && "Commissions must be whole percents from 1 to 80",
    !name.trim() && "Name the invite",
    !email.trim() && "Add a contact email creators can reach you on",
    overLimit.length > 0 && `Remove ${overLimit.length} creator${overLimit.length === 1 ? "" : "s"} over your follower limit`,
    creators.length > remaining && `Your weekly outreach has ${remaining} creator${remaining === 1 ? "" : "s"} left`,
  ].filter(Boolean) as string[];

  const send = useMutation({
    mutationFn: () =>
      createTikTokCollaboration({
        name: name.trim(),
        message: message.trim() || undefined,
        endTime: new Date(`${endDate}T23:59:59`).toISOString(),
        products: selectedProducts.map((p) => ({
          id: p.id,
          commission: Number(terms[p.id].commission),
          adsCommission: terms[p.id].adsCommission.trim() ? Number(terms[p.id].adsCommission) : undefined,
        })),
        creatorOpenIds: creatorIds,
        sellerContact: { email: email.trim() },
        freeSample,
        sampleApprovalExempt: freeSample && autoApprove,
        preferredContentType: contentType === "ANY" ? undefined : contentType,
      }),
    onSuccess: (result) => {
      const skipped = new Set(result.conflicts.map((c) => c.creatorOpenId)).size + result.invalidCreatorOpenIds.length;
      const sent = creators.length - skipped;
      toast.success(`Invite sent to ${sent} creator${sent === 1 ? "" : "s"}`);
      if (skipped > 0) toast(`${skipped} creator${skipped === 1 ? " was" : "s were"} skipped: already invited to these products or unavailable`, { icon: "⚠️" });
      onCreated(result.id);
      onOpenChange(false);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const toggleCreator = (c: TikTokCreatorPick) =>
    setCreators((prev) =>
      prev.some((x) => x.openId === c.openId) ? prev.filter((x) => x.openId !== c.openId) : prev.length >= MAX_CREATORS ? prev : [...prev, c],
    );
  const pickable = [...creators, ...suggested.filter((s) => !creators.some((c) => c.openId === s.openId))].filter(
    (c) => !filter.trim() || `${c.username} ${c.nickname ?? ""}`.toLowerCase().includes(filter.trim().toLowerCase()),
  );
  const conflictNames = (conflicts.data?.conflicts ?? []).map((c) => {
    const who = creators.find((x) => x.openId === c.creatorOpenId)?.username ?? c.creatorOpenId;
    const what = products.data?.find((p) => p.id === c.productId)?.title ?? c.productId;
    return `@${who} · ${what}`;
  });
  const block = products.error ? blockFromError(products.error) : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col overflow-y-auto sm:max-w-xl">
        <SheetHeader className="text-left">
          <SheetTitle>New collab invite</SheetTitle>
          <SheetDescription>
            A private offer on TikTok: a higher commission, and free samples if you like, for the creators you pick. They accept it in TikTok’s Creator Center.
          </SheetDescription>
        </SheetHeader>

        {block ? (
          <TikTokBlockedState block={block} feature="collab invites" onRetry={() => products.refetch()} />
        ) : (
          <div className="mt-5 flex-1 space-y-7">
            {quota.data && !quota.data.unlimited && (
              <div className="rounded-lg border border-border px-3 py-2.5">
                <div className="flex items-baseline justify-between text-xs">
                  <span className="font-medium text-foreground">Weekly outreach</span>
                  <span className="tabular-nums text-muted-foreground">
                    {quota.data.remaining ?? "?"} of {quota.data.total ?? "?"} left · resets {shortDate(quota.data.periodEnd)}
                  </span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-brand-500"
                    style={{ width: `${Math.min(100, ((quota.data.used ?? 0) / Math.max(1, quota.data.total ?? 1)) * 100)}%` }}
                  />
                </div>
                {maxFollowers !== null && (
                  <p className="mt-1.5 text-[11px] text-muted-foreground">TikTok lets your shop reach creators with up to {formatCompactCount(maxFollowers)} followers.</p>
                )}
              </div>
            )}

            <section className="space-y-2">
              <div className="flex items-baseline justify-between">
                <h3 className="text-sm font-semibold text-foreground">1. Creators</h3>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {creators.length} of {MAX_CREATORS}
                </span>
              </div>
              {creators.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {creators.map((c) => (
                    <span
                      key={c.openId}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-full border py-0.5 pl-0.5 pr-1.5 text-xs",
                        overLimit.includes(c) ? "border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-300" : "border-border bg-card",
                      )}
                    >
                      <UserAvatar src={c.avatarUrl} name={c.nickname || c.username} className="h-5 w-5 text-[9px]" />@{c.username}
                      <button type="button" aria-label={`Remove @${c.username}`} onClick={() => toggleCreator(c)} className="text-muted-foreground hover:text-foreground">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input className="pl-9" placeholder="Filter creators you work with" value={filter} onChange={(e) => setFilter(e.target.value)} />
              </div>
              <div className="max-h-56 overflow-y-auto rounded-lg border border-border">
                {pickable.length === 0 ? (
                  <p className="px-3 py-6 text-center text-xs text-muted-foreground">No creators yet. Find some in Social Search.</p>
                ) : (
                  pickable.map((c) => {
                    const checked = creators.some((x) => x.openId === c.openId);
                    return (
                      <label key={c.openId} className="flex cursor-pointer items-center gap-3 border-b border-border px-3 py-2 last:border-b-0 hover:bg-muted/40">
                        <Checkbox checked={checked} onCheckedChange={() => toggleCreator(c)} />
                        <UserAvatar src={c.avatarUrl} name={c.nickname || c.username} className="h-7 w-7 text-[10px]" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-foreground">{c.nickname || `@${c.username}`}</span>
                          <span className="block truncate text-xs text-muted-foreground">
                            @{c.username}
                            {c.followerCount != null ? ` · ${formatCompactCount(c.followerCount)} followers` : ""}
                          </span>
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Suggestions are creators who requested your samples.{" "}
                <Link to="/brand/social-search#creators" className="text-brand-700 hover:underline dark:text-brand-400">
                  Find more in Social Search
                </Link>
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">2. Products and commission</h3>
              {products.isLoading ? (
                <SpinnerBlock className="py-8" />
              ) : (products.data ?? []).length === 0 ? (
                <p className="text-xs text-muted-foreground">No products in an open collaboration yet. Add them to open collaboration in TikTok Seller Center first.</p>
              ) : (
                <div className="rounded-lg border border-border">
                  {products.data!.map((p) => {
                    const t = terms[p.id];
                    if (!t) return null;
                    const set = (patch: Partial<ProductTerms>) => setTerms((prev) => ({ ...prev, [p.id]: { ...prev[p.id], ...patch } }));
                    return (
                      <div key={p.id} className="flex flex-wrap items-center gap-3 border-b border-border px-3 py-2.5 last:border-b-0">
                        <Checkbox checked={t.selected} onCheckedChange={(v) => set({ selected: v === true })} aria-label={`Include ${p.title}`} />
                        {p.imageUrl ? (
                          <img src={p.imageUrl} alt="" className="h-9 w-9 rounded-md object-cover" />
                        ) : (
                          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-muted">
                            <Package className="h-4 w-4 text-muted-foreground" />
                          </span>
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-foreground">{p.title}</span>
                          <span className="block text-xs text-muted-foreground">
                            {p.priceMin ? `$${p.priceMin}` : ""}
                            {p.openCommission !== null ? ` · open rate ${p.openCommission}%` : ""}
                            {p.inventory === 0 ? " · out of stock" : ""}
                          </span>
                        </span>
                        {t.selected && (
                          <span className="flex items-center gap-2">
                            <label className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Input className="h-8 w-14 text-right" inputMode="numeric" value={t.commission} onChange={(e) => set({ commission: e.target.value })} aria-label="Commission percent" />%
                            </label>
                            <label className="flex items-center gap-1 text-xs text-muted-foreground" title="Paid on orders from ads that use the creator’s video">
                              ads
                              <Input className="h-8 w-14 text-right" inputMode="numeric" placeholder="—" value={t.adsCommission} onChange={(e) => set({ adsCommission: e.target.value })} aria-label="Ads commission percent" />%
                            </label>
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {conflictNames.length > 0 && (
                <div className="flex gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    Already in another invite, so TikTok will skip these pairs: {conflictNames.slice(0, 4).join("; ")}
                    {conflictNames.length > 4 ? ` and ${conflictNames.length - 4} more` : ""}.
                  </span>
                </div>
              )}
            </section>

            <section className="space-y-4">
              <h3 className="text-sm font-semibold text-foreground">3. Terms</h3>
              <div className="space-y-1">
                <Label htmlFor="invite-name" className="text-xs font-normal text-muted-foreground">
                  Name (creators see it)
                </Label>
                <Input id="invite-name" maxLength={100} value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="invite-end" className="text-xs font-normal text-muted-foreground">
                    Ends
                  </Label>
                  <Input id="invite-end" type="date" min={isoDate(new Date(Date.now() + 86_400_000))} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="invite-email" className="text-xs font-normal text-muted-foreground">
                    Contact email
                  </Label>
                  <Input id="invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="invite-message" className="flex justify-between text-xs font-normal text-muted-foreground">
                  Message
                  <span className="tabular-nums">
                    {message.length}/{MESSAGE_MAX}
                  </span>
                </Label>
                <Textarea id="invite-message" rows={3} maxLength={MESSAGE_MAX} value={message} onChange={(e) => setMessage(e.target.value)} />
              </div>
              <div className="flex items-start justify-between gap-4">
                <span>
                  <span className="block text-sm text-foreground">Offer free samples</span>
                  <span className="block text-xs text-muted-foreground">Invited creators can request one of each product.</span>
                </span>
                <Switch checked={freeSample} onCheckedChange={setFreeSample} />
              </div>
              <div className="flex items-start justify-between gap-4">
                <span>
                  <span className={cn("block text-sm", freeSample ? "text-foreground" : "text-muted-foreground")}>Approve their sample requests automatically</span>
                  <span className="block text-xs text-muted-foreground">Skips your review for creators in this invite.</span>
                </span>
                <Switch checked={freeSample && autoApprove} disabled={!freeSample} onCheckedChange={setAutoApprove} />
              </div>
              <div className="space-y-1.5">
                <span className="text-xs text-muted-foreground">Preferred content</span>
                <div className="inline-flex rounded-lg border border-border bg-muted p-0.5">
                  {(["ANY", "VIDEO", "LIVE"] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setContentType(t)}
                      className={cn("rounded-md px-3 py-1 text-sm", contentType === t ? "bg-card text-foreground shadow-sm" : "text-muted-foreground")}
                    >
                      {t === "ANY" ? "Any" : t === "VIDEO" ? "Video" : "LIVE"}
                    </button>
                  ))}
                </div>
              </div>
            </section>
          </div>
        )}

        <SheetFooter className="mt-6 flex-col gap-2 sm:flex-col">
          {problems.length > 0 && !block && <p className="text-right text-xs text-muted-foreground">{problems[0]}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="button" disabled={problems.length > 0 || send.isPending || !!block} onClick={() => send.mutate()}>
              {send.isPending ? "Sending…" : `Send invite${creators.length ? ` to ${creators.length}` : ""}`}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
